import { Injectable } from "@angular/core";
import "rxjs/add/operator/map";
import { Observable } from "rxjs/Rx";

// Classes.
import { ResponseData } from "../fetching/wa-response/wa-api-response";

// Interfaces.
import { LocatorInterface } from "../interfaces/locator.interface";

// Providers.
import { WABaseService } from "./wa-base.service";
import { LoggerService } from "../logger/logger-service";
import { EnvironmentService } from "./environment-service";
import { FactoryService } from "./factory-service";
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, catchError, tap } from 'rxjs/operators/';
import {
  ParamBuilderService,
  ParamSpecStruct,
  RequestedItems
} from "./param-builder-service";
import { of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DataFetchService extends WABaseService {
  constructor(
    public http: HttpClient,
    protected logger: LoggerService,
    protected env: EnvironmentService,
    protected factory: FactoryService,
    protected paramBuilder: ParamBuilderService
  ) {
    super(logger, env);
  }

  protected fetchHelper(
    params: HttpParams,
    url: string
  ): Observable<ResponseData> {
    this.log("Fetching URL " + url + "/" + params.toString());

    const headers = new HttpHeaders().append(
      "Client-Security-Token",
      "8a9aeaee-69b6-4d7b-81fa-8ca3f93adb6c"
    );

    return (
      this.http
        .get(url, { headers: headers, params: params })
        .pipe(
          map(response => new ResponseData(response)),

        )
    );
  }

  /*****************************************************************************
   * Functions related to fetching
   ***************************************************************************/
  public requestCategory(
    requestedItems: RequestedItems,
    locator: LocatorInterface
  ): Observable<ResponseData> {
    const paramSpecs = [new ParamSpecStruct("CatCode", "category")];

    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "CATEGORIES");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }

  public requestCountryNames(
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    let params = this.paramBuilder.build(null, null, requestedItems);

    params = params.append("content", "COUNTRIES");
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }

  public requestRecordList(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    const paramSpecs = [
      new ParamSpecStruct("RecordLevel", "recordLevel"), //
      new ParamSpecStruct("CatCode", "category"), //
      new ParamSpecStruct("DisciplineId", "disciplineId"), //
      new ParamSpecStruct("Team", "team"), //
      new ParamSpecStruct("ParaArchery", "isPara"), //
      new ParamSpecStruct("Historical", "isHistorical"), //
      new ParamSpecStruct("fText", "search"),
      new ParamSpecStruct("SortBy", "sortrank")
    ];
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "RECORDS");
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }

  public requestWCupRanking(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    const paramSpecs = [
      new ParamSpecStruct("DisciplineId", "discipline"),
      new ParamSpecStruct("CatCode", "category"),
      new ParamSpecStruct("Year", "year"),
      new ParamSpecStruct("Team", "team"),
      new ParamSpecStruct("Detailed", "detailed"),
      new ParamSpecStruct("fText", "search"),
      new ParamSpecStruct("SortBy", "sortrank"),
      new ParamSpecStruct('Country', 'country')
    ];
    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "WORLDCUPRANKINGS");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }

  public requestWCupRankingCategories(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    const paramSpecs = [
      new ParamSpecStruct("DisciplineId", "discipline"),
      new ParamSpecStruct("Year", "year")
    ];

    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "WORLDCUPRANKINGSCATEGORIES");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }

  public requestWorldRanking(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    const paramSpecs = [
      new ParamSpecStruct("CatCode", "category"),
      new ParamSpecStruct("Date", "date"),
      new ParamSpecStruct("Team", "team"),
      new ParamSpecStruct("Detailed", "detailed"),
      new ParamSpecStruct("Year", "year"),
      new ParamSpecStruct("fText", "search"),
      new ParamSpecStruct("SortBy", "sortrank"),
      new ParamSpecStruct('Country', 'country')
    ];

    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "WORLDRANKINGS");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }

  public requestWorldRankingCategories(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    const paramSpecs = [
      new ParamSpecStruct("Team", "team"),
      new ParamSpecStruct("Date", "date"),
      new ParamSpecStruct("ParaArchery", "isPara")
    ];

    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "WORLDRANKINGSCATEGORIES");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }

  public requestAthleteList(
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    let params = this.paramBuilder.build(null, null, requestedItems);
    params = params.append("content", "ATHLETES");

    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }

  public requestAthlete(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    const paramSpecs = [new ParamSpecStruct("Id", "athlete")];
    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "ATHLETEBIOGRAPHY");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }

  public requestAthleteResultList(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    const paramSpecs = [
      new ParamSpecStruct("Id", "athlete"),
      new ParamSpecStruct("LevelId", "level"),
      new ParamSpecStruct("StartDate", "startDate"),
      new ParamSpecStruct("EndDate", "endDate"),
      new ParamSpecStruct("DisciplineId", "discipline")
    ];

    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "ATHLETERESULTS");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }

  public requestEventList(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    let paramSpecs = [
      new ParamSpecStruct("LevelId", "level"),
      new ParamSpecStruct("StartDate", "startDate"),
      new ParamSpecStruct("EndDate", "endDate"),
      new ParamSpecStruct("CompId", "competition"),
      new ParamSpecStruct("EventTypeId", "event"),
      new ParamSpecStruct("Country", "country"),
      new ParamSpecStruct("CatCode", "category"),
      new ParamSpecStruct("CatId", "catId"),
      new ParamSpecStruct("Featured", "featured"),
      new ParamSpecStruct("WithResults", "withRes"),
      new ParamSpecStruct("Live", "live"),
      new ParamSpecStruct("DisciplineId", "discipline"),
      new ParamSpecStruct("fText", "search"),
      new ParamSpecStruct("SortBy", "sort"),
      new ParamSpecStruct('Detailed', 'detailed'),
      new ParamSpecStruct("WorldRecordStatus", "worldrecordstatus"),
      new ParamSpecStruct("WorldRankingEvent", "worldrankingevent"),
      new ParamSpecStruct("embedFiles", "embedfiles"),
      new ParamSpecStruct('DivId', "catDivision")


    ];

    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value['nameInside']));
        }
      }
    })
    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "COMPETITIONS");
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }

  public requestLatestNews(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    let endpointFragment: string;
    if (locator.getComponent("popular").toString() === "popular") {
      endpointFragment = "news-popular";
    } else {
      endpointFragment = "news";
    }

    const url = `${this.env.cmsServerURL}/api/v1.0/${endpointFragment}`;

    return this.http.get(url, {}).pipe(map((response: any) => {
      let items = response;
      let responseDataRaw = {
        items: items,
        pageInfo: {
          totalResults: items.length,
          resultsPerPage: 10,
          page: 0
        }
      }
      return new ResponseData(responseDataRaw);
    }))
  }
  public requestSeriesStage(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    const paramSpecs = [
      new ParamSpecStruct("DisciplineId", "discipline"),
      new ParamSpecStruct("Year", "year"),
      new ParamSpecStruct("fText", "search")
    ];

    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "SERIESSTAGES");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }
  public requestSeriesAmateursRankings(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    const paramSpecs = [
      new ParamSpecStruct("CatId", "catid"),
      new ParamSpecStruct("Year", "year"),
      new ParamSpecStruct("CatCode", "category"),
      new ParamSpecStruct("fText", "search"),
      new ParamSpecStruct("Detailed", "detailed"),
      new ParamSpecStruct("SortBy", "sortindoor")
    ];

    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "SERIESAMATEURSRANKINGS");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }
  public requestSeriesTeamRanking(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    const paramSpecs = [
      new ParamSpecStruct("CatId", "catid"),
      new ParamSpecStruct("Year", "year"),
      new ParamSpecStruct("CatCode", "category"),
      new ParamSpecStruct("fText", "search"),
      new ParamSpecStruct("SortBy", "sortindoor"),
      new ParamSpecStruct("Detailed", "detailed")
    ];
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "SERIESTEAMSRANKINGS");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }
  public requestWCupSeason(
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    let params = this.paramBuilder.build(null, null, requestedItems);
    params = params.append("content", "WORLDCUPSEASON");

    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }
  public requestRecordsArea(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    let paramSpecs = [new ParamSpecStruct("RecordLevel", "catid")];

    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "RECORDAREAS");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    let url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }
  public requestRecordHistory(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    let paramSpecs = [
      new ParamSpecStruct("RecordLevel", "recordLevel"),
      new ParamSpecStruct("RecordArea", "recordArea"),
      new ParamSpecStruct("RecordType", "recordType")
    ];

    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "RECORDHISTORY");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    let url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }
  public requestSeriesList(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    const paramSpecs = [
      new ParamSpecStruct("DisciplineId", "discipline"),
      new ParamSpecStruct("Year", "year"),
      new ParamSpecStruct("fText", "search")
    ];

    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "SERIES");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }
  public requestSeriesProList(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    const paramSpecs = [
      new ParamSpecStruct("CatId", "catid"),
      new ParamSpecStruct("Year", "year"),
      new ParamSpecStruct("CatCode", "category"),
      new ParamSpecStruct("fText", "search"),
      new ParamSpecStruct("Detailed", "detailed"),
      new ParamSpecStruct("SortBy", "sortindoor"),
      new ParamSpecStruct("Team", "team")
    ];

    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "SERIESPRORANKINGS");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }
  public requestCompetitionDisciplines(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    const paramSpecs = [

    ];

    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "COMPETITIONDISCIPLINES");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }
  public requestCompetitionLevels(
    locator: LocatorInterface,
    requestedItems: RequestedItems
  ): Observable<ResponseData> {
    const paramSpecs = [

    ];

    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "COMPETITIONLEVELS");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }
  public requestDivisionCategory(
    requestedItems: RequestedItems,
    locator: LocatorInterface
  ): Observable<ResponseData> {
    const paramSpecs = [];

    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "DIVISIONS");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }
  public requestPrizeForPrecisionCategory(
    requestedItems: RequestedItems,
    locator: LocatorInterface
  ): Observable<ResponseData> {
    const paramSpecs = [new ParamSpecStruct("Year", "year")];

    let params = this.paramBuilder.build(paramSpecs, locator, requestedItems);
    params = params.append("content", "PRECISIONPRIZECATEGORIES");
    locator.getComponents().forEach(value => {
      if (value.getParam() != null) {
        const index = paramSpecs.findIndex(param => param.name === value.getParam());
        if (index === -1) {
          this.log("WARNING, Locator " + value.toString() + " with param " + value.getParam() + " it's not fixed to request ", params);
          paramSpecs.push(new ParamSpecStruct(value.getParam() as any, value.toString()));
        }
      }
    })
    const url = `${this.env.waApiServerURL}`;
    return this.fetchHelper(params, url);
  }
}
